import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext();

const MAX_TOASTS = 5;
const DEFAULT_DURATION = 5000;

let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    // Clear the auto-remove timer if it exists
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message, type = 'info', duration = DEFAULT_DURATION) => {
    const id = ++toastIdCounter;

    const newToast = {
      id,
      message,
      type,
      duration,
      createdAt: Date.now()
    };

    setToasts((prev) => {
      // If we are at the maximum, remove the oldest toast(s) to make room
      let updated = [...prev];
      while (updated.length >= MAX_TOASTS) {
        const oldest = updated[0];
        if (timersRef.current[oldest.id]) {
          clearTimeout(timersRef.current[oldest.id]);
          delete timersRef.current[oldest.id];
        }
        updated = updated.slice(1);
      }
      return [...updated, newToast];
    });

    // Set auto-remove timer
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const value = {
    toasts,
    addToast,
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
